// apps/web/src/main.jsx
// Migrated from src/react/main.jsx – boot logic in @tennis-matches/services/bootstrap
import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ReactApp } from "./reactApp.jsx";
import {
  initStore,
  getState,
  setState,
  subscribe,
} from "@tennis-matches/core/store";
import { loadState } from "@tennis-matches/services/persistence";
import { defaultState } from "@tennis-matches/core";
import { navigateTo } from "@tennis-matches/core/router";
import { installMobileWebBridgeClient } from "@tennis-matches/services/platform";
import { bootstrapApp } from "@tennis-matches/services/bootstrap";
import {
  syncPasswordRecoveryIntentFromBrowserUrl,
  normalizePkceAuthCallbackPathInBrowser,
} from "@tennis-matches/services/auth";
import { initTheme } from "./utils/theme.js";
import { devMark, devPerfEnabled } from "./utils/devPerf.js";

// CRITICAL: Import CSS in correct order (core → ui → ui-components → app)
// Embedded apps (Schleifchen, etc.) don't load their own CSS; we need component styles here.
import "@tennis-matches/core/styles/index.css";
import "@tennis-matches/ui/styles";
import "@tennis-matches/ui/styles/tokens-tennis.css";
import "@tennis-matches/ui/styles/components/platform.css";
import "@tennis-matches/ui-components/styles/index.css";
import "@tennis-matches/ui/styles/pages/hub.css";
import "@tennis-matches/ui/styles/pages/app-hub.css";
import "@tennis-matches/ui/styles/pages/settings.css";
import "@tennis-matches/ui/styles/pages/matchmixer.css";
import "@tennis-matches/ui/styles/pages/trainerstunden.css";
import "@tennis-matches/ui/styles/pages/public-invoices.css";
import "@tennis-matches/ui/styles/pages/public-booking.css";
import "@tennis-matches/clubmeisterschaft-core/ui/pages/clubmeisterschaft-landing.css";
import "@tennis-matches/ui/styles/pages/react-ios.css";
/* Wizard v0 (tm-wizard-v0__*) – gleiche CSS wie Schleifchen-Setup, für ClubmeisterschaftSetupPage + ggf. eingebettetes Schleifchen-Setup */
import "@tennis-matches/ui/wizard/wizard-v0.css";
import "./styles/app.css";
import "./styles/ai-panel.css";
import "./styles/clubchamp-simple.css";

initTheme();

// Passwort-Recovery aus E-Mail-Link (#type=recovery / ?next=/reset-password) vor React-Bootstrap merken.
// PKCE von falscher Site-URL (ohne /auth/callback) auf kanonischen Callback normalisieren.
if (!normalizePkceAuthCallbackPathInBrowser()) {
  syncPasswordRecoveryIntentFromBrowserUrl();
}

// Nach Deploy: veraltete lazy Chunks → Browser lädt HTML statt JS. Einmal neu laden.
(function installChunkLoadRecovery() {
  const key = "tm:chunk-reload";
  const reloadOnce = () => {
    try {
      if (sessionStorage.getItem(key) === "1") return;
      sessionStorage.setItem(key, "1");
    } catch (_) {}
    window.location.reload();
  };
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    reloadOnce();
  });
  window.setTimeout(() => {
    try {
      sessionStorage.removeItem(key);
    } catch (_) {}
  }, 3000);
})();

const el = document.getElementById("app");
if (!el) throw new Error('[web] root "#app" not found');

// Minimal boot (shared state shape) — keeps React app functional during migration.
let initial = null;
try {
  initial = loadState();
} catch (_) {}
try {
  initStore(initial || defaultState);
} catch (e) {
  console.warn("[web] initStore failed, continuing with null state:", e);
}

const mobileBridge = installMobileWebBridgeClient({ navigateTo });
const effectiveNavigateTo = mobileBridge?.navigateTo || navigateTo;

bootstrapApp({
  mode: "web",
  getState,
  setState,
  subscribe,
  navigateTo: effectiveNavigateTo,
  onI18nUpdated: null,
});

if (devPerfEnabled()) {
  devMark("tm-web-create-root");
}

createRoot(el).render(
  <React.StrictMode>
    <BrowserRouter
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <ReactApp />
    </BrowserRouter>
  </React.StrictMode>,
);

if (devPerfEnabled()) {
  devMark("tm-web-root-render-scheduled");
  try {
    requestAnimationFrame(() => {
      devMark("tm-web-first-frame-after-render");
    });
  } catch (_) {}
}
